import styled, { css } from 'styled-components';
import {
  zIndex,
  ButtonWithStates,
  Link
} from '@comicrelief/component-library';
import Fieldset from '../../components/Fieldset/Fieldset';

import * as assets from './assets';

const maxWidth = '1152px';

const TopScrollWrapper = styled.div`
  position: absolute;
  top: -4.5rem;
`;

const FsuMainWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  &:after {
    // Preload audience images
    content: url(${assets.publicImg}) url(${assets.schoolImg}) url(${assets.workImg}) none;
  }
`;

const FormOuterWrapper = styled.div`
  padding: 2rem 0;
  @media ${({ theme }) => theme.allBreakpoints('L')} {
    padding: 4rem 2rem;
  }
`;

const FormInnerWrapper = styled.div`
  ${zIndex('base')}
  background-color: ${({ theme }) => theme.color('white')};
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.15);
  position: relative;

  margin: 0 auto;
  padding: 2rem;

  @media ${({ theme }) => theme.allBreakpoints('M')} {
    padding: 3rem;
    border-radius: 1rem;
  }

  @media ${({ theme }) => theme.allBreakpoints('L')} {
    max-width: ${maxWidth};
  }

  input,
  select {
    max-width: 100%;
  }
`;

const IntroTextWrapper = styled.div`
  margin-bottom: 2rem;

  @media ${({ theme }) => theme.allBreakpoints('M')} {
    width: 50%;
    margin-bottom: 4rem;
  }
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  margin: 60px 0 42px;
  background-color: ${({ theme }) => theme.color('grey_medium')};
`;

const SubmitContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2rem;
`;

const SubmitButton = styled(ButtonWithStates)`
  padding-left: 3rem;
  padding-right: 3rem;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.15);
  margin: 0;
  width: 100%;
`;

// 'Soft' hides its children - i.e. sets 'display: none' on the container, keeping them in the dom
// (e.g. so that form inputs keep their values)
const SoftHider = styled.div`
  display: ${({ show }) => (show ? 'block' : 'none')};
`;

const DescriptionWrapper = styled.div`
  padding-bottom: 1.5rem;
`;

const FsuDropShadowStyles = css`
  box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.15);
  border-radius: 1rem 1rem;
`;

const JustGivingFieldset = styled(Fieldset)`
  border: 0;
  margin: 0 0 2rem 0;
  padding: 2.5rem;
  ${FsuDropShadowStyles}
  position: relative;
`;

const JustGivingPageLinkLoaderWrapper = styled.div`
  width: 100%;
  text-align: left;
  padding: 2.5rem;
  margin-top: 3rem;
  ${FsuDropShadowStyles}
  position: relative;
  span {
    text-align: center; /* this is to allow the loading spinner to be centered */
  }
`;

const MarketingPreferencesContainer = styled.div`
  padding: 2.5rem;
  ${FsuDropShadowStyles}

  #marketing-preferences--marketing_prefs > div {
    margin-top: 0;
  }
`;

const JustGivingRegisterHeading = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 16px;
`;

const ActivateJustGivingButton = styled(Link)`
  background-color: #7A04DD;
  width: 100%;
  margin-top: 0.5rem;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1.2;

  // To deal with huge amount of copy; this will be a global
  // Button update shortly, so this can be removed then.
  height: auto;
  min-height: 2.5rem;

  &:hover,
  &:focus {
    background-color: #470082;
  }
`;

const JustGivingPill = styled.div`
  background-color: #7A04DD;
  width: 133px;
  height: 36px;
  padding: 14px 11px 11px;
  border-radius: 8px;
  position: absolute;
  top: -18px; /* Half the height to center on the top border */
  right: 20px; /* 20px from right edge on mobile */
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    height: auto;
    width: 100%;
    object-fit: contain;
  }

  @media ${({ theme }) => theme.allBreakpoints('M')} {
    right: 40px; /* 40px from right edge on desktop */
  }
`;

const ErrorIcon = styled.span`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  color: ${({ theme }) => theme.color('black')};
  background-color: ${({ theme }) => theme.color('white')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.75rem;
  vertical-align: middle;

  svg {
    filter: brightness(0) invert(1);
  }
`;

const FundraisingGroupsWrapper = styled.div`
  margin-top: 60px;
`;

// To make the scroll less ugly and close to the top of the copy
const ScrollWrapper = styled.div`
  padding-top: 30px;
`;

const FundraisingPackWrapper = styled.div`
  margin-top: 60px;
`;

const RadioButtonWrapper = styled.div`
  // adds gap between children
  > * + * {
    margin-top: 16px;
  }
`;

const JustGivingWrapper = styled(JustGivingPageLinkLoaderWrapper)`
  padding: 0;
  margin-top: 60px;
`;

const MarketingPreferencesWrapper = styled.div`
  margin-top: 60px;
  padding: 0 2.5rem 2.5rem 2.5rem;
  ${FsuDropShadowStyles}
`;

const TermsConditionsWrapper = styled.div`
  margin: 30px 0;
  padding: 12px;
`;

const AddressFieldsWrapper = styled.div`
  margin-top: 20px;
  display: grid;
  // The gap between the Address1 wrapper and the wrapper for the other fields:
  gap: 1rem;

  @media ${({ theme }) => theme.allBreakpoints('M')} {
    margin-top: 20px;

    // Other fields wrapper:
    > div:nth-child(2) {
      // Address 3 and Town
       > label:nth-child(3),
      > label:nth-child(4) {
        margin-top: 10px;
      }
    }
  }
`;

/**
 * FundraisingGroupSection styles
 */
const FundGroupWrapper = styled.div`
  margin-top: 60px;
`;

const SchoolFieldsWrapper = styled.div`
  margin-top: 30px;
  display: grid;
  gap: 1rem;
`;

const OrgFieldWrapper = styled.div`
  margin-top: 30px;
`;

const WorkFieldsWrapper = styled.div`
  margin-top: 1rem;
  display: grid;
  gap: 1rem;
`;

const PublicFieldsWrapper = styled.div`
  margin-top: 30px;
`;

const ClubFieldsWrapper = styled.div`
  margin-top: 1rem;
`;

const LookupWrapper = styled.div`
  margin-top: 1rem;
  button {
    margin-top: 20px;
  }
`;

export {
  ScrollWrapper,
  TopScrollWrapper,
  FsuMainWrapper,
  FormOuterWrapper,
  FormInnerWrapper,
  IntroTextWrapper,
  Divider,
  SubmitContainer,
  SubmitButton,
  SoftHider,
  DescriptionWrapper,
  FsuDropShadowStyles,
  FundraisingPackWrapper,
  JustGivingFieldset,
  JustGivingRegisterHeading,
  MarketingPreferencesWrapper,
  JustGivingPageLinkLoaderWrapper,
  MarketingPreferencesContainer,
  ActivateJustGivingButton,
  JustGivingPill,
  ErrorIcon,
  FundraisingGroupsWrapper,
  JustGivingWrapper,
  TermsConditionsWrapper,
  AddressFieldsWrapper,
  FundGroupWrapper,
  SchoolFieldsWrapper,
  PublicFieldsWrapper,
  ClubFieldsWrapper,
  OrgFieldWrapper,
  WorkFieldsWrapper,
  LookupWrapper,
  RadioButtonWrapper
};
